import { Box, Divider, IconButton, Typography, useTheme } from "@mui/material";
import { Button } from "@mui/material";
import MenuPopover from "src/components/MenuPopover";
import useLocales from "src/hooks/useLocales";
import NotificationAlertBox from "./NotificationAlertBoxV2";
import useNotifications, { readNotification } from "./useNotifications";
import { useState } from "react";
import BellIcon from "src/assets/icon/Bell";
import { Badge } from "@mui/material";

const NotificationPopover = () => {
  const { translate } = useLocales();
  const { notifications, setNotifications } = useNotifications();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handlePopoverClose = (e) => {
    console.log("handlePopoverClose");
    e.stopPropagation();
    setAnchorEl(null);
  };
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          handlePopoverOpen(e);
        }}
      >
        <Badge
          color="error"
          variant="dot"
          invisible={notifications?.length == 0}
          sx={{
            "& .MuiBadge-badge": {
              width: 8,
              height: 8,
              bgcolor: "error.dark",
            },
          }}
        >
          <BellIcon
            height={20}
            width={20}
            customColor={theme.palette.text.label}
          />
        </Badge>
      </IconButton>
      <MenuPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        sx={{
          mt: 1.5,
          ml: 0.75,
          width: 350,
          "& .MuiMenuItem-root": {
            typography: "body2",
            borderRadius: 0.5,
          },
          bgcolor: "background.paper",
          p: 1,
          boxShadow: "0px 6px 12px -3px #424a530a, 0px 6px 18px 0px #424a531f", // "0px 2px 5px 0px rgba(0,0,0,0.12)",
          border: `1px solid #d0d7de`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            pt: 1,
          }}
        >
          <Typography variant="subtitle1">
            {translate("Notifications")}
          </Typography>
          {notifications?.length > 0 && (
            <Button
              size="small"
              onClick={() => {
                notifications?.forEach((noti) => {
                  readNotification(noti.id);
                });
                setNotifications([]);
              }}
              sx={{
                fontSize: 10,
                px: 0.5,
                "&:hover": {
                  color: "text.secondary",
                  textDecoration: "underline",
                },
              }}
            >
              {translate("Mark all as read")}
            </Button>
          )}
        </Box>
        <Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          {notifications?.length > 0 ? (
            <>
              <NotificationAlertBox
                displayALl
                notifications={[...notifications]}
                setNotifications={setNotifications}
              />
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 170,
                width: "100%",
              }}
            >
              <svg
                width="50"
                height="50"
                viewBox="0 0 180 180"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <ellipse
                  opacity="0.1"
                  cx="90"
                  cy="174.6"
                  rx="90"
                  ry="5.4"
                  fill="#2B2A7F"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M82.3482 38.8894C82.3482 35.3053 85.2537 32.3999 88.8377 32.3999C92.4218 32.3999 95.3272 35.3053 95.3272 38.8894C95.3272 40.7183 94.5706 42.3706 93.3533 43.5502C111.468 45.2453 124.582 57.196 126.188 75.2633C129.852 73.1859 134.088 71.9999 138.6 71.9999C152.518 71.9999 163.8 83.2823 163.8 97.1999C163.8 109.68 154.728 120.041 142.82 122.048C143.566 123.183 144 124.541 144 126C144 129.976 140.776 133.2 136.8 133.2H106.134C105.393 141.459 98.4523 147.932 90 147.932C81.5477 147.932 74.6073 141.459 73.8656 133.2H43.2C39.2236 133.2 36 129.976 36 126C36 122.023 39.2236 118.8 43.2 118.8H49.5229V82.6581C49.5229 62.4564 64.7593 45.8133 84.3675 43.5937C83.1237 42.4114 82.3482 40.741 82.3482 38.8894ZM122.714 77.2582C122.72 77.3747 122.735 77.4887 122.76 77.5991C117.051 82.219 113.4 89.2831 113.4 97.1999C113.4 106.367 118.295 114.39 125.613 118.8H53.1229V82.6581C53.1229 62.9328 69.1134 46.9423 88.8386 46.9423C107.957 46.9423 121.876 58.582 122.714 77.2582ZM77.5276 133.532C78.401 139.638 83.6524 144.332 90 144.332C96.3477 144.332 101.599 139.638 102.472 133.532H77.5276ZM39.6 41.1942C39.6 40.3137 40.3138 39.5999 41.1943 39.5999C42.0748 39.5999 42.7886 40.3137 42.7886 41.1942C42.7886 42.0747 42.0748 42.7885 41.1943 42.7885C40.3138 42.7885 39.6 42.0747 39.6 41.1942ZM41.1943 35.9999C38.3256 35.9999 36 38.3255 36 41.1942C36 44.0629 38.3256 46.3885 41.1943 46.3885C44.063 46.3885 46.3886 44.0629 46.3886 41.1942C46.3886 38.3255 44.063 35.9999 41.1943 35.9999ZM138.6 75.5999C126.671 75.5999 117 85.2705 117 97.1999C117 109.129 126.671 118.8 138.6 118.8C150.529 118.8 160.2 109.129 160.2 97.1999C160.2 85.2705 150.529 75.5999 138.6 75.5999ZM138.6 80.9999C136.115 80.9999 134.1 83.0146 134.1 85.4999V96.2999C134.1 98.7852 136.115 100.8 138.6 100.8C141.085 100.8 143.1 98.7852 143.1 96.2999V85.4999C143.1 83.0146 141.085 80.9999 138.6 80.9999ZM137.7 85.4999C137.7 85.0028 138.103 84.5999 138.6 84.5999C139.097 84.5999 139.5 85.0028 139.5 85.4999V96.2999C139.5 96.797 139.097 97.1999 138.6 97.1999C138.103 97.1999 137.7 96.797 137.7 96.2999V85.4999ZM134.1 108.9C134.1 111.385 136.115 113.4 138.6 113.4C141.085 113.4 143.1 111.385 143.1 108.9C143.1 106.415 141.085 104.4 138.6 104.4C136.115 104.4 134.1 106.415 134.1 108.9ZM138.6 109.8C138.103 109.8 137.7 109.397 137.7 108.9C137.7 108.403 138.103 108 138.6 108C139.097 108 139.5 108.403 139.5 108.9C139.5 109.397 139.097 109.8 138.6 109.8ZM61.0644 42.3311C61.4459 42.155 61.8974 42.3286 62.0628 42.7149L62.1187 42.8454C62.3042 43.279 62.1099 43.7959 61.669 43.9994C54.6903 47.2202 50.584 52.1491 48.2195 56.2593C47.0356 58.3174 46.2875 60.1723 45.8367 61.5065C45.6115 62.1733 45.4608 62.709 45.3672 63.0737C45.3205 63.256 45.288 63.3955 45.2677 63.487C45.2576 63.5328 45.2505 63.5666 45.2461 63.5877L45.2445 63.5955L45.2426 63.6049L45.3109 63.3006L45.2391 63.6228L45.2307 63.6668C45.1467 64.108 44.7179 64.3951 44.278 64.3047C43.8469 64.2162 43.5668 63.7979 43.6492 63.3655L43.6736 63.2373L43.6741 63.2349L43.6749 63.2305L43.6764 63.2229L43.6842 63.1843L43.6848 63.1815C43.6911 63.1508 43.7002 63.108 43.7122 63.0539C43.737 62.9421 43.7744 62.7817 43.8267 62.5777C43.9313 62.1697 44.0957 61.5869 44.3378 60.8702C44.8219 59.4374 45.6185 57.4654 46.8734 55.2838C49.3869 50.9144 53.7354 45.7135 61.0644 42.3311ZM40.1382 62.5692L40.1128 62.6919C39.6621 65.0575 41.1947 67.3466 43.5537 67.8311C45.961 68.3256 48.3073 66.7546 48.7671 64.3404L48.7729 64.3101L48.7823 64.2666C48.7945 64.2118 48.8178 64.1106 48.8544 63.968C48.9276 63.6824 49.0532 63.2333 49.2473 62.6588C49.6367 61.5063 50.2937 59.8735 51.3401 58.0544C53.4232 54.4332 57.0238 50.1082 63.1775 47.2681C65.3753 46.2538 66.3863 43.6672 65.4283 41.4289L65.3724 41.2983C64.4092 39.048 61.7784 38.0367 59.5559 39.0624C51.4013 42.8258 46.5472 48.6311 43.7529 53.4887C42.3604 55.9093 41.4727 58.1034 40.9272 59.7179C40.6539 60.527 40.4645 61.1963 40.3396 61.6834C40.2771 61.9271 40.2305 62.1258 40.1976 62.2742C40.1812 62.3485 40.1681 62.4102 40.1581 62.4587L40.1568 62.4649L40.0932 62.802L40.125 62.6335L40.2098 62.2197L40.1568 62.4649L40.1382 62.5692Z"
                  fill={theme.palette.action.disabled}
                />
              </svg>
              <Typography
                variant={"body2"}
                sx={{ px: 1, py: 2, textAlign: "center" }}
              >
                {translate("No new notifications")}
              </Typography>
            </Box>
          )}
        </Box>
        <Divider
          sx={{
            color: "divider",
            mb: 2.5,
          }}
        />
      </MenuPopover>
    </>
  );
};

export default NotificationPopover;
